import type { SignupSurveyValue } from '@/lib/attribution/signup-survey'
import type { PlanKey } from '@/lib/billing/constants'
import type { DeclineReason } from '@/lib/growth/actions/decline-reasons'
import type { RecipeOutcome } from '@/lib/growth/recipe/types'
import type { FreeScanLeadEntry } from '@/lib/tools/free-scan-lead-request'

/**
 * Typed analytics event registry (DAT-18, ARC-21).
 *
 * Every PostHog event name this app fires from onboarding — and the billing
 * surfaces onboarding hands off to — is declared here, once. Before this
 * file every call site spelled the event name as an inline string literal;
 * a typo or a second spelling of the same event splits a funnel invisibly,
 * and nothing catches it because the string only exists at the call site.
 *
 * `onboarding_step_viewed` and `onboarding_scan_completed` are declared
 * ahead of their call sites — the onboarding v3 rebuild wires them in a
 * later task. `onboarding_completed` fires **server-side only**
 * (`webhooks/polar/route.ts`) and is not sent through `captureAnalyticsEvent`
 * — it is registered here so its name and payload shape have one home too.
 * `subscription_canceled` is likewise **server-side only**, fired from the
 * same webhook when a customer cancels in Polar (with their reason/comment).
 * `signup_source_answered` is **server-side only** too, fired from
 * `POST /api/onboarding/signup-survey` the first time a user answers "How did you hear about us?".
 * `suggestion_accepted`, `suggestion_declined`, `suggestion_reason_given` and `suggestion_done` are
 * **server-side only** (R6.17 B4, `lib/analytics/server-capture.ts`): sent after the response from
 * `PATCH /api/growth/actions` and `PUT /api/growth/actions/[id]/decline-reason`, so an ad blocker
 * cannot drop them and a failed send never fails the user's action. `suggestion_viewed` and
 * `suggestion_opened` are the opposite, **client-side only** (R6.17 B5, `lib/analytics/suggestion-view-
 * guards.ts`): a view and a click are only known in the browser, and neither is sent before the visitor
 * has answered the analytics banner.
 */

/** The full set of registered event names. Keys and values are identical by construction. */
export type AnalyticsEventName =
  | 'onboarding_step_viewed'
  | 'onboarding_scan_completed'
  | 'onboarding_recipe_viewed'
  | 'onboarding_completed'
  | 'onboarding_step_failed'
  | 'domain_submitted'
  | 'brand_confirmed'
  | 'competitor_added'
  | 'competitor_removed'
  | 'plan_selected'
  | 'checkout_embed_failed'
  | 'subscription_wait_resolved'
  | 'subscription_canceled'
  | 'signup_source_answered'
  | 'deploy_skew_reload_prompt'
  | 'free_scan_started'
  | 'free_scan_completed'
  | 'free_scan_failed'
  | 'free_scan_recipe_viewed'
  | 'free_scan_cta_clicked'
  | 'free_scan_shared'
  | 'free_scan_email_submitted'
  | 'free_scan_email_sent'
  | 'free_scan_email_failed'
  | 'suggestion_viewed'
  | 'suggestion_opened'
  | 'suggestion_accepted'
  | 'suggestion_declined'
  | 'suggestion_reason_given'
  | 'suggestion_done'

/** The free scan's funnel events (R6.6 AC-11): every name above that starts `free_scan_`. */
export type FreeScanEventName = Extract<AnalyticsEventName, `free_scan_${string}`>

// growth-engine-r1c/U5: 'crawl' (step 2) and 'state' (step 5) added per
// ROADMAP.md §1.18's table — see onboarding/_components/step-state.ts for
// the full renumbering.
export type OnboardingStepName =
  | 'domain'
  | 'crawl'
  | 'brand'
  | 'competitors'
  | 'state'
  | 'results'
  | 'plan'

export interface OnboardingStepViewedProperties {
  step: 1 | 2 | 3 | 4 | 5 | 6 | 7
  step_name: OnboardingStepName
}

/**
 * Fired from steps 1-4 (domain, crawl, brand, competitors) wherever those
 * screens already report to Sentry — the same failure, given a PostHog-side
 * home so it sits in the same funnel as the `onboarding_step_viewed` it
 * interrupted. Steps 5-7 (state, results, plan) keep their own dedicated
 * failure signals (results' terminal-state capture, `checkout_embed_failed`'s
 * `stage`) rather than folding into this.
 */
export interface OnboardingStepFailedProperties {
  step: 1 | 2 | 3 | 4
  step_name: Extract<OnboardingStepName, 'domain' | 'crawl' | 'brand' | 'competitors'>
  reason: string
}

export interface DomainSubmittedProperties {
  mode: 'create' | 'edit'
}

/**
 * `corrected` is true when the saved brand name or market differs from what
 * the crawl-derived analysis proposed — the signal for "how often is our
 * auto-detected brand identity wrong".
 */
export interface BrandConfirmedProperties {
  corrected: boolean
}

export type CompetitorOrigin = 'suggested' | 'manual'

export interface CompetitorAddedProperties {
  origin: CompetitorOrigin
}

export interface CompetitorRemovedProperties {
  origin: CompetitorOrigin
}

export interface OnboardingScanCompletedProperties {
  /**
   * Step 6's Non-branded value as a whole percent — the figure step 6 and step 7 show (R6.3 AC-3).
   * `null` when the step's safety timeout fired before the scan finished: no figure existed, and
   * none is computed from partial rows. Also `null` for a population with no measured answer.
   */
  visibility_score: number | null
  invisible_prompt_count: number
  /** `null` alongside a `null` `visibility_score` — the competitor figure was never read. */
  has_top_competitor: boolean | null
  engines_completed: number
  duration_ms: number
}

/**
 * Step 6's recipe entered view (R6.5b AC-7), once per run. Carries the outcome, how many actions
 * the recipe shows and whether any of them is worked. Never an email, a domain, a brand or a URL.
 */
interface OnboardingRecipeViewedProperties {
  outcome: RecipeOutcome
  action_count: number
  worked_steps_shown: boolean
}

export interface OnboardingCompletedProperties {
  /**
   * `'unknown'` when the Polar product id on the webhook payload resolves to
   * no known plan. That is a real state and it is reported as itself — never
   * coerced to a plan we did not measure, which would silently inflate that
   * plan's conversions in the one funnel this registry exists to make
   * readable (PRD-02, PRD-03).
   */
  plan: PlanKey | 'unknown'
  $set_once: { activated_at: string }
}

export type PlanSelectedSource = 'onboarding' | 'settings'

export interface PlanSelectedProperties {
  plan: PlanKey
  interval: 'month' | 'year'
  source: PlanSelectedSource
}

/**
 * Where in the checkout handoff the failure happened. Reported as a property
 * on ONE event name rather than one event per stage, so the drop-off reads
 * as a single funnel in PostHog instead of unrelated counters.
 */
export type CheckoutFailureStage =
  | 'session_create'
  | 'missing_url'
  | 'embed_create'
  | 'embed_timeout'
  | 'subscription_wait_timeout'

export interface CheckoutEmbedFailedProperties {
  stage: CheckoutFailureStage
  plan: PlanKey
  interval: 'month' | 'year'
  reason: string
}

/** Elapsed time from checkout success to the subscription row reaching an
 * entitled status — pairs with `checkout_embed_failed`'s
 * `subscription_wait_timeout` stage to close the loop on the PR #180 race. */
export interface SubscriptionWaitResolvedProperties {
  duration_ms: number
}

/**
 * A customer cancelled a subscription or trial in Polar (`subscription.canceled`,
 * fired server-side from `webhooks/polar/route.ts`). `cancellation_reason` is
 * Polar's reason as sent, or `'none_given'` when the customer picked none;
 * `cancellation_comment` is their trimmed free text, or null. Both come from
 * the same `toCancellationRecord()` value the webhook writes onto the
 * `subscriptions` row, so the event and the row cannot disagree. `$set`
 * keeps the person property this capture has always set.
 */
interface SubscriptionCanceledProperties {
  plan: PlanKey | 'unknown'
  polar_subscription_id: string
  cancellation_reason: string
  cancellation_comment: string | null
  $set: { plan: 'churned' }
}

/**
 * growth-engine-r6.10 AC-5: the optional onboarding answer, sent once, only for a real answer (a
 * skip is recorded in the database and sends nothing). `answer_other` is the free text, trimmed
 * and capped at 100 characters, and is non-null only for `answer: 'other'`.
 */
interface SignupSourceAnsweredProperties {
  answer: SignupSurveyValue
  answer_other: string | null
  $set_once: { signup_said: SignupSurveyValue }
}

/**
 * The free scan's funnel (R6.6 AC-11). Every `free_scan_*` event is captured on the server, keyed
 * by `free-scan:<scan_id>`, so one PostHog funnel reads one scan's steps as one person. None of
 * these payloads has an `email`, `ip` or `ip_hash` key, on purpose: the helper's type is this
 * registry's, so adding one is a compile error.
 */
interface FreeScanBaseProperties {
  /** The `free_tool_usage` row id: "a scan" is the row. */
  scan_id: string
  /** Which surface started the scan. */
  entry: FreeScanLeadEntry
}

/** The call-to-action buttons whose click is reported; the beacon's zod enum is this array. */
export const FREE_SCAN_CTAS = [
  'result_cta',
  'audit_cta',
  'audit_loading',
  'audit_sticky_bar',
] as const
type FreeScanCta = (typeof FREE_SCAN_CTAS)[number]

/** The ways a visitor shares a result; the beacon's zod enum is this array too. */
export const FREE_SCAN_SHARE_METHODS = ['copy', 'x', 'linkedin'] as const
type FreeScanShareMethod = (typeof FREE_SCAN_SHARE_METHODS)[number]

interface FreeScanStartedProperties extends FreeScanBaseProperties {
  domain: string
}

interface FreeScanCompletedProperties extends FreeScanBaseProperties {
  domain: string
  /** The engines asked, as stored on the scan. */
  engines: string[]
  /** Questions that named the brand. */
  named: number
  /** Questions that were answered (named or not): the denominator of "named in N of M". */
  measured: number
  /** The recipe's outcome; `null` when the recipe build threw. */
  outcome: RecipeOutcome | null
  action_count: number
  duration_ms: number
}

/** A scan that died: `prompts` (none generated), `update` (the complete write failed) or `unexpected`. */
interface FreeScanFailedProperties extends FreeScanBaseProperties {
  domain: string
  stage: 'prompts' | 'update' | 'unexpected'
}

interface FreeScanCtaClickedProperties extends FreeScanBaseProperties {
  cta: FreeScanCta
}

interface FreeScanSharedProperties extends FreeScanBaseProperties {
  method: FreeScanShareMethod
}

interface FreeScanEmailSubmittedProperties extends FreeScanBaseProperties {
  marketing_consent: boolean
  /** True when the scan was still running and the send waits for it. */
  queued: boolean
}

interface FreeScanEmailFailedProperties extends FreeScanBaseProperties {
  reason: 'send_failed' | 'suppressed' | 'content' | 'scan_failed'
}

/**
 * The Suggestion funnel's server events (R6.17 AC-5, AC-6). Every payload names the same Suggestion
 * (`suggestion_id`, the `growth_actions` row), the check that raised it (`detector_id`, null for a
 * hand-made Task) and the project, so one PostHog funnel follows a Suggestion from viewed to done. None
 * has an `email`, `name` or free-text key, on purpose: adding one is a compile error. The "Something
 * else" text stays in the database; PostHog gets `has_comment` only.
 */
interface SuggestionEventBase {
  suggestion_id: string
  detector_id: string | null
  project_id: string
}

/** Where a Suggestion was declined: from the Suggestions list or Topic detail, or from the Plan. */
type SuggestionDeclinedFrom = 'suggestion' | 'plan'

/** Where a card was on screen: Growth → Suggestions, a Topic's findings, or Overview's next-actions rows. */
export type SuggestionSurface = 'suggestions' | 'topic' | 'overview'

/**
 * At least half of a Suggestion's card (or Overview row) stayed on screen for one second (R6.17 AC-4).
 * Sent from the browser, once per Suggestion per browser; `surface` is where it happened.
 */
interface SuggestionViewedProperties extends SuggestionEventBase {
  surface: SuggestionSurface
}

/**
 * "Show details" opened, or a Suggestions page arrived at from an Overview row with the details already
 * open: once per Suggestion per page visit. `via` is `overview` only for that arrival.
 */
interface SuggestionOpenedProperties extends SuggestionViewedProperties {
  via: 'overview' | null
}

interface SuggestionDeclinedProperties extends SuggestionEventBase {
  /** Read from the row just written; null only inside the deploy window, before the column exists. */
  declined_from: SuggestionDeclinedFrom | null
}

interface SuggestionReasonGivenProperties extends SuggestionEventBase {
  reason: DeclineReason
  has_comment: boolean
  declined_from: SuggestionDeclinedFrom | null
}

interface SuggestionDoneProperties extends SuggestionEventBase {
  /** A member's Mark as done, or the weekly check completing the Task by itself. */
  by: 'member' | 'check'
}

/** The Suggestion funnel's browser events (R6.17 B5): sent through `captureAnalyticsEvent`. */
type SuggestionClientEventName = 'suggestion_viewed' | 'suggestion_opened'

/** The Suggestion funnel's server events: every `suggestion_` name the browser does not send. */
export type SuggestionEventName = Exclude<
  Extract<AnalyticsEventName, `suggestion_${string}`>,
  SuggestionClientEventName
>

/** Payload type for every registered event, keyed by its event name. */
export interface AnalyticsEventProperties {
  onboarding_step_viewed: OnboardingStepViewedProperties
  onboarding_scan_completed: OnboardingScanCompletedProperties
  onboarding_recipe_viewed: OnboardingRecipeViewedProperties
  onboarding_completed: OnboardingCompletedProperties
  onboarding_step_failed: OnboardingStepFailedProperties
  domain_submitted: DomainSubmittedProperties
  brand_confirmed: BrandConfirmedProperties
  competitor_added: CompetitorAddedProperties
  competitor_removed: CompetitorRemovedProperties
  plan_selected: PlanSelectedProperties
  checkout_embed_failed: CheckoutEmbedFailedProperties
  subscription_wait_resolved: SubscriptionWaitResolvedProperties
  subscription_canceled: SubscriptionCanceledProperties
  signup_source_answered: SignupSourceAnsweredProperties
  deploy_skew_reload_prompt: DeploySkewReloadPromptProperties
  free_scan_started: FreeScanStartedProperties
  free_scan_completed: FreeScanCompletedProperties
  free_scan_failed: FreeScanFailedProperties
  free_scan_recipe_viewed: FreeScanBaseProperties
  free_scan_cta_clicked: FreeScanCtaClickedProperties
  free_scan_shared: FreeScanSharedProperties
  free_scan_email_submitted: FreeScanEmailSubmittedProperties
  free_scan_email_sent: FreeScanBaseProperties
  free_scan_email_failed: FreeScanEmailFailedProperties
  suggestion_viewed: SuggestionViewedProperties
  suggestion_opened: SuggestionOpenedProperties
  suggestion_accepted: SuggestionEventBase
  suggestion_declined: SuggestionDeclinedProperties
  suggestion_reason_given: SuggestionReasonGivenProperties
  suggestion_done: SuggestionDoneProperties
}

/**
 * An error boundary showed the "A new version of RankSurf is available" reload prompt for a
 * Server Action from an older deployment (RANKSURF-8G). The per-deploy rate lives here, not in
 * Sentry quota. `route` is a route template (`routeTemplate()`: id and token segments → `:id`).
 */
interface DeploySkewReloadPromptProperties {
  route: string
}

/**
 * The registry itself. `satisfies Record<keyof AnalyticsEventProperties, string>`
 * makes an event declared in one map but not the other a compile error —
 * add an event here and its properties type above, in the same change.
 */
export const ANALYTICS_EVENTS = {
  onboarding_step_viewed: 'onboarding_step_viewed',
  onboarding_scan_completed: 'onboarding_scan_completed',
  onboarding_recipe_viewed: 'onboarding_recipe_viewed',
  onboarding_completed: 'onboarding_completed',
  onboarding_step_failed: 'onboarding_step_failed',
  domain_submitted: 'domain_submitted',
  brand_confirmed: 'brand_confirmed',
  competitor_added: 'competitor_added',
  competitor_removed: 'competitor_removed',
  plan_selected: 'plan_selected',
  checkout_embed_failed: 'checkout_embed_failed',
  subscription_wait_resolved: 'subscription_wait_resolved',
  subscription_canceled: 'subscription_canceled',
  signup_source_answered: 'signup_source_answered',
  deploy_skew_reload_prompt: 'deploy_skew_reload_prompt',
  free_scan_started: 'free_scan_started',
  free_scan_completed: 'free_scan_completed',
  free_scan_failed: 'free_scan_failed',
  free_scan_recipe_viewed: 'free_scan_recipe_viewed',
  free_scan_cta_clicked: 'free_scan_cta_clicked',
  free_scan_shared: 'free_scan_shared',
  free_scan_email_submitted: 'free_scan_email_submitted',
  free_scan_email_sent: 'free_scan_email_sent',
  free_scan_email_failed: 'free_scan_email_failed',
  suggestion_viewed: 'suggestion_viewed',
  suggestion_opened: 'suggestion_opened',
  suggestion_accepted: 'suggestion_accepted',
  suggestion_declined: 'suggestion_declined',
  suggestion_reason_given: 'suggestion_reason_given',
  suggestion_done: 'suggestion_done',
} as const satisfies Record<keyof AnalyticsEventProperties, string>

/**
 * Structural subset of the PostHog client's `capture()` — avoids a hard
 * dependency on posthog-js's types. Declared as a method (not an arrow-typed
 * property) so it is checked bivariantly against the real client's more
 * specific `properties` parameter type.
 */
interface AnalyticsCaptureClient {
  capture(event: string, properties?: unknown): void
}

/**
 * Fires a registered event through a browser PostHog client (`usePostHog()`),
 * with the event name and its property shape both resolved from the registry
 * — never a loose string at the call site.
 *
 * **Client-side only.** `posthog-node`'s `capture()` takes a single object
 * (`{ distinctId, event, properties }`), so `getPostHogClient()` does not
 * satisfy `AnalyticsCaptureClient`. A server-side send reads its name from
 * `ANALYTICS_EVENTS` and its payload type from `AnalyticsEventProperties`
 * directly — that is how `onboarding_completed` fires from the Polar webhook.
 */
export function captureAnalyticsEvent<E extends AnalyticsEventName>(
  client: AnalyticsCaptureClient,
  event: E,
  properties: AnalyticsEventProperties[E]
): void {
  client.capture(ANALYTICS_EVENTS[event], properties)
}
